<script setup lang="ts">
import AppContextRail from '@/components/app-shell/AppContextRail.vue'
import FinestCountBadge from '@/components/FinestCountBadge.vue'
import FinestGauge from '@/components/FinestGauge.vue'
import FinestGaugeCompact from '@/components/FinestGaugeCompact.vue'
import { estateObjectAddressLine } from '@/utils/estate-object'
import EstateObjectsController from '@controllers/Investors/EstateObjectsController'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import EstateObjectData = App.Data.EstateObject.EstateObjectData

const props = defineProps<{
  estateObjects: EstateObjectData[]
  missingFieldCounts: Record<string, number>
  completion: number
}>()

const { t } = useI18n()

const listLimit = 4

const incomplete = computed(() =>
  props.estateObjects
    .map((estateObject) => ({
      id: estateObject.id ?? '',
      addressLine: estateObjectAddressLine(estateObject),
      city: estateObject.city ?? '',
      missingFields: props.missingFieldCounts[estateObject.id ?? ''] ?? 0,
    }))
    .filter((entry) => entry.missingFields > 0)
    .sort((left, right) => right.missingFields - left.missingFields),
)

const listed = computed(() => incomplete.value.slice(0, listLimit))

const hiddenCount = computed(() => incomplete.value.length - listed.value.length)

const readyCount = computed(() => props.estateObjects.length - incomplete.value.length)

const missingTotal = computed(() => incomplete.value.reduce((total, entry) => total + entry.missingFields, 0))
</script>

<template>
  <AppContextRail v-if="estateObjects.length">
    <FinestGaugeCompact :value="completion" :label="t('gauge.label', { percent: completion })" />
  </AppContextRail>

  <div v-if="estateObjects.length" class="flex flex-col gap-4">
    <FinestGauge :value="completion" :caption="t('gauge.caption')" :label="t('gauge.label', { percent: completion })" />

    <div
      v-if="incomplete.length"
      class="border-warning-subtle-active bg-warning-subtle rounded-[10px] border px-4 py-3">
      <p class="text-sm font-bold">{{ t('gap.headline', { count: incomplete.length }) }}</p>
      <p class="text-ink-2 mt-0.5 text-xs">
        {{ t('gap.ready', { ready: readyCount, total: estateObjects.length }) }} ·
        {{ t('gap.missing', { count: missingTotal }) }}
      </p>
    </div>

    <Message v-else severity="success" icon="pi pi-check-circle" size="small">
      <span class="text-sm">{{ t('allReady') }}</span>
    </Message>

    <div v-if="listed.length" class="border-line-2 border-t pt-4">
      <p class="text-ink mb-3 font-medium">{{ t('list.title') }}</p>

      <ul class="flex flex-col gap-1">
        <li v-for="entry in listed" :key="entry.id">
          <Link
            :href="EstateObjectsController.show(entry.id)"
            class="hover:bg-surface-hover group flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors">
            <FinestCountBadge
              :count="entry.missingFields"
              :aria-label="t('list.missing', { count: entry.missingFields })"
              class="flex-none" />
            <span class="min-w-0 flex-1">
              <span class="text-ink block truncate text-sm font-medium">{{ entry.addressLine }}</span>
              <span class="text-ink-3 block truncate text-xs">{{ entry.city }}</span>
            </span>
            <i
              class="pi pi-chevron-right text-ink-3 group-hover:text-action flex-none text-xs transition-colors"
              aria-hidden="true" />
          </Link>
        </li>
      </ul>

      <p v-if="hiddenCount" class="text-ink-3 mt-2 px-2 text-xs">{{ t('list.more', { count: hiddenCount }) }}</p>
    </div>
  </div>
</template>

<i18n>
{
  "de": {
    "gauge": {
      "caption": "bankreif",
      "label": "{percent} % bankreif"
    },
    "gap": {
      "headline": "Bei {count} Objekten fehlen Angaben | Bei einem Objekt fehlen Angaben | Bei {count} Objekten fehlen Angaben",
      "ready": "{ready} von {total} Objekten vollständig",
      "missing": "{count} Angaben offen | {count} Angabe offen | {count} Angaben offen"
    },
    "allReady": "Alle Objekte sind vollständig erfasst. Deine Bestandsliste ist bankreif.",
    "list": {
      "title": "Größte Lücken",
      "missing": "{count} Angaben fehlen | {count} Angabe fehlt | {count} Angaben fehlen",
      "more": "und {count} weitere Objekte"
    }
  },
  "en": {
    "gauge": {
      "caption": "bank-ready",
      "label": "{percent} % bank-ready"
    },
    "gap": {
      "headline": "{count} properties are missing details | One property is missing details | {count} properties are missing details",
      "ready": "{ready} of {total} properties complete",
      "missing": "{count} details open | {count} detail open | {count} details open"
    },
    "allReady": "Every property is fully captured. Your portfolio list is bank-ready.",
    "list": {
      "title": "Biggest gaps",
      "missing": "{count} details missing | {count} detail missing | {count} details missing",
      "more": "and {count} more properties"
    }
  }
}
</i18n>
